import React, { useEffect, useState } from "react";
import { createBrand, fetchBrands, toggleBrandStatus, updateBrand } from "../../services/brandApi";
import { fetchCategories } from "../../services/deviceApi";
import TableScroll from "../../components/TableScroll";
import "../../styles/admin-common.css";

const emptyForm = { name: "", logoUrl: "", displayOrder: "", categoryIds: [] };

const Brands = () => {
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [brandList, categoryList] = await Promise.all([
        fetchBrands(),
        fetchCategories(),
      ]);
      setBrands(brandList);
      setCategories(categoryList || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const toggleCategory = (categoryId) => {
    setForm((prev) => {
      const exists = prev.categoryIds.includes(categoryId);
      return {
        ...prev,
        categoryIds: exists
          ? prev.categoryIds.filter((id) => id !== categoryId)
          : [...prev.categoryIds, categoryId],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");
    setError("");
    const payload = {
      name: form.name,
      logoUrl: form.logoUrl,
      displayOrder: form.displayOrder ? parseInt(form.displayOrder, 10) : undefined,
      categoryIds: form.categoryIds,
    };
    try {
      if (editingId) {
        await updateBrand({ brandId: editingId, ...payload });
        setSuccess("Brand updated.");
      } else {
        await createBrand(payload);
        setSuccess("Brand created.");
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const startEdit = (brand) => {
    setEditingId(brand.id);
    setForm({
      name: brand.name,
      logoUrl: brand.logoUrl || "",
      displayOrder: brand.displayOrder != null ? String(brand.displayOrder) : "",
      categoryIds: Array.isArray(brand.categoryIds) ? [...brand.categoryIds] : [],
    });
  };

  const handleToggle = async (brandId) => {
    setSuccess("");
    setError("");
    try {
      await toggleBrandStatus(brandId);
      setSuccess("Brand status updated.");
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-page">
      <h2>Brand Management</h2>
      <p className="subtitle">
        Create and manage device brands. Assign product types (Mobile, Watch, …)
        so the app only shows brands for the selected category.
      </p>
      {error && <p className="error-msg">{error}</p>}
      {success && <p className="success-msg">{success}</p>}

      <div className="admin-split-layout">
        <div className="admin-card">
          <h3>{editingId ? "Edit Brand" : "Add Brand"}</h3>
          <form className="admin-form" onSubmit={handleSubmit}>
            <label className="field-label">
              Brand name
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </label>
            <label className="field-label">
              Logo URL
              <input
                value={form.logoUrl}
                onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
                required
              />
            </label>
            <label className="field-label">
              Display order
              <input
                type="number"
                value={form.displayOrder}
                onChange={(e) => setForm({ ...form, displayOrder: e.target.value })}
              />
            </label>
            <fieldset className="field-label" style={{ border: "none", padding: 0, margin: 0 }}>
              <legend style={{ fontWeight: 600, marginBottom: 8 }}>Product types</legend>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                {categories.length === 0 && (
                  <span className="empty-msg">No categories loaded</span>
                )}
                {categories.map((cat) => (
                  <label key={cat.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={form.categoryIds.includes(cat.id)}
                      onChange={() => toggleCategory(cat.id)}
                    />
                    {cat.name}
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="form-actions">
              <button type="submit" className="admin-btn">{editingId ? "Update" : "Create"}</button>
              {editingId && (
                <button type="button" className="admin-btn secondary" onClick={resetForm}>Cancel</button>
              )}
            </div>
          </form>
        </div>

        <div className="admin-card">
          <h3>Brands</h3>
          {loading ? <div className="admin-loading">Loading brands</div> : (
            <TableScroll>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Logo</th>
                  <th>Types</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {brands.length === 0 && (
                  <tr><td colSpan={6} className="empty-msg">No brands yet</td></tr>
                )}
                {brands.map((brand) => (
                  <tr key={brand.id}>
                    <td>{brand.name}</td>
                    <td>
                      {brand.logoUrl && (
                        <img src={brand.logoUrl} alt={brand.name} className="brand-logo" />
                      )}
                    </td>
                    <td>
                      {(brand.categoryNames && brand.categoryNames.length > 0)
                        ? brand.categoryNames.join(", ")
                        : "—"}
                    </td>
                    <td>{brand.displayOrder ?? "—"}</td>
                    <td className={brand.isActive ? "badge-active" : "badge-inactive"}>
                      {brand.isActive ? "Active" : "Inactive"}
                    </td>
                    <td className="actions-cell">
                      <button type="button" className="admin-btn secondary" onClick={() => startEdit(brand)}>Edit</button>
                      <button type="button" className="admin-btn secondary" onClick={() => handleToggle(brand.id)}>
                        {brand.isActive ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </TableScroll>
          )}
        </div>
      </div>
    </div>
  );
};

export default Brands;
